import React, { useState } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import SubscriptionService from "../../../services/SubscriptionService";
import UserService from "../../../services/UserService";
import { WORD_WHEEL_PACK_OFFERS, wordWheelPackCheckoutPlan } from "./packOffers";

function PackBox({ offer, owned, busy, onLock, onPlay }) {
    const Icon = offer.icon;
    const [detailsOpen, setDetailsOpen] = useState(false);

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                p: 1,
                pr: offer.popup ? 0.5 : 1,
                borderRadius: 2,
                border: "1px solid rgba(125, 211, 252, 0.4)",
                bgcolor: "rgba(15, 55, 75, 0.72)",
                color: "#f0f9ff",
            }}
        >
            {offer.popup ? (
                <IconButton
                    aria-label={`${offer.name} details`}
                    onClick={() => setDetailsOpen(true)}
                    sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        width: 44,
                        height: 44,
                        color: "#bae6fd",
                        fontWeight: 800,
                        fontSize: "1.15rem",
                    }}
                >
                    ?
                </IconButton>
            ) : null}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.5, pr: offer.popup ? 4.5 : 0 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.25,
                        bgcolor: "rgba(56, 189, 248, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        "& svg": { fontSize: 22 },
                    }}
                >
                    <Icon />
                </Box>
                <Box sx={{ minWidth: 0, pt: 0.25 }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1, rowGap: 0.25 }}>
                        <Typography sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: "1rem" }}>{offer.name}</Typography>
                        {offer.levelCount ? (
                            <Typography
                                component="span"
                                sx={{ fontWeight: 700, fontSize: "0.85rem", lineHeight: 1.2, color: "#7dd3fc", whiteSpace: "nowrap" }}
                            >
                                {offer.levelCount} levels
                            </Typography>
                        ) : null}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.35, opacity: 0.9, lineHeight: 1.35 }}>
                        {offer.shopLine}
                    </Typography>
                </Box>
            </Box>
            <Button
                variant="contained"
                disabled={busy}
                onClick={() => (owned ? onPlay(offer) : onLock(offer))}
                startIcon={owned ? <PlayArrowIcon /> : <LockOutlinedIcon />}
                sx={{
                    minHeight: 44,
                    mt: "auto",
                    textTransform: "none",
                    fontWeight: 800,
                    color: "#e2e8f0",
                    bgcolor: "#3e4c5e",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#4b5c70", boxShadow: "none" },
                    "&.Mui-disabled": { color: "#e2e8f0", bgcolor: "#3e4c5e", opacity: 0.7 },
                }}
            >
                {owned ? "Play" : `Unlock ${offer.price}`}
            </Button>
            {offer.popup ? (
                <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle>{offer.name}</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>{offer.shopLine}</Typography>
                        {offer.levelCount ? (
                            <Typography sx={{ fontWeight: 800, mb: 1 }}>{offer.levelCount} levels</Typography>
                        ) : null}
                        <Typography>{offer.popup}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDetailsOpen(false)} sx={{ minHeight: 44 }}>Close</Button>
                    </DialogActions>
                </Dialog>
            ) : null}
        </Box>
    );
}

export default function WordWheelPackShop({ signedIn, ownedCodes }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [busyCode, setBusyCode] = useState("");
    const [error, setError] = useState("");

    const goLogin = () => {
        navigate("/login", {
            state: { from: { pathname: `${location.pathname}${location.search || ""}` } },
        });
    };

    const onLock = async (offer) => {
        if (!signedIn) {
            goLogin();
            return;
        }
        setBusyCode(offer.code);
        setError("");
        try {
            const userData = await UserService.getUserInfo();
            const email = userData?.user?.email || userData?.cloudUser?.email;
            if (!email) {
                setError("Could not start checkout.");
                return;
            }
            const result = await SubscriptionService.createCheckoutSession(wordWheelPackCheckoutPlan(offer), email);
            const url = result?.data?.url || result?.data?.checkoutUrl;
            if (!result.success || !url) {
                setError(result.error || "Could not start checkout.");
                return;
            }
            window.location.assign(url);
        } catch (checkoutError) {
            setError(checkoutError?.message || "Could not start checkout.");
        } finally {
            setBusyCode("");
        }
    };

    const onPlay = (offer) => {
        if (offer.code === "daily_bundle") {
            navigate("/puzzles/word-wheel/daily");
            return;
        }
        navigate(`/puzzles/word-wheel/pack/${offer.code}`);
    };

    return (
        <Box>
            {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                }}
            >
                {WORD_WHEEL_PACK_OFFERS.map((offer) => (
                    <PackBox
                        key={offer.code}
                        offer={offer}
                        owned={ownedCodes.has(offer.code)}
                        busy={busyCode === offer.code}
                        onLock={onLock}
                        onPlay={onPlay}
                    />
                ))}
            </Box>
        </Box>
    );
}
